import { STORAGE_KEYS } from '@/constants/storageKeys'
import { useCallStore } from '@/store/callStore'
import { usePresenceStore } from '@/store/presenceStore'
import { getPendingEndedOrderReleaseID, releaseEndedOrder, shouldSkipUnloadRelease } from './anchorOrderRelease'
import { markCallOrderEnded } from './callOrderLifecycleGuard'
import { getEnvConfig } from './runtime-env'
import { shengwangClient } from './shengwangClient'

export const ANCHOR_PAGE_UNLOAD_REASON = 'anchor_page_unload'
const HIDDEN_CALL_CLEANUP_DELAY_MS = 300

interface PageUnloadReleasePayload {
  access_token: string
  event_type: 'call_ended'
  reason_code: typeof ANCHOR_PAGE_UNLOAD_REASON
}

function buildReleaseRequest(orderID: string) {
  const baseURL = getEnvConfig().API_BASE_URL
  const token = localStorage.getItem(STORAGE_KEYS.accessToken)
  if (!baseURL || !orderID || !token) {
    return null
  }

  const payload: PageUnloadReleasePayload = {
    access_token: token,
    event_type: 'call_ended',
    reason_code: ANCHOR_PAGE_UNLOAD_REASON,
  }

  return {
    url: `${baseURL}/orders/${encodeURIComponent(orderID)}/release-unload`,
    body: JSON.stringify(payload),
  }
}

function acceptedOutgoingOrderID() {
  const outgoingCall = useCallStore.getState().outgoingCall
  if (outgoingCall?.status === 'accepted') {
    return outgoingCall.order_id || ''
  }
  return ''
}

export function releaseCurrentCallOnPageUnload() {
  if (typeof window === 'undefined') {
    return false
  }

  const calls = useCallStore.getState()
  const currentOrderID = calls.currentCall?.order_id || ''
  const activeOrderID = shengwangClient.getActiveCall()?.order_id || ''
  const outgoingAcceptedOrderID = acceptedOutgoingOrderID()
  const pendingReleaseOrderID = getPendingEndedOrderReleaseID()
  const orderID = currentOrderID || activeOrderID || outgoingAcceptedOrderID || pendingReleaseOrderID
  if (shouldSkipUnloadRelease(orderID)) {
    return false
  }
  const request = buildReleaseRequest(orderID)
  if (!request) {
    return false
  }
  markCallOrderEnded(orderID)

  try {
    const body = new Blob([request.body], { type: 'text/plain;charset=UTF-8' })
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function' &&
      navigator.sendBeacon(request.url, body)
    ) {
      return true
    }

    if (typeof fetch !== 'function') {
      return false
    }
    void fetch(request.url, {
      method: 'POST',
      body: request.body,
      keepalive: true,
      headers: {
        'Content-Type': 'text/plain;charset=UTF-8',
      },
    }).catch(() => {
      void releaseEndedOrder(orderID, ANCHOR_PAGE_UNLOAD_REASON)
    })
    return true
  } catch {
    return false
  }
}

export function registerPageUnloadCallCleanup() {
  let cleanedUp = false
  let hiddenCleanupTimer: ReturnType<typeof setTimeout> | null = null
  const clearHiddenCleanupTimer = () => {
    if (hiddenCleanupTimer !== null) {
      clearTimeout(hiddenCleanupTimer)
      hiddenCleanupTimer = null
    }
  }
  const cleanupMedia = (event?: Event) => {
    if (
      event &&
      'persisted' in event &&
      (event as PageTransitionEvent).persisted
    ) {
      clearHiddenCleanupTimer()
      return
    }
    if (cleanedUp) {
      return
    }
    clearHiddenCleanupTimer()
    cleanedUp = true
    const calls = useCallStore.getState()
    const orderID = calls.currentCall?.order_id || shengwangClient.getActiveCall()?.order_id || acceptedOutgoingOrderID()
    releaseCurrentCallOnPageUnload()
    shengwangClient.emergencyCloseMedia()
    void shengwangClient.leaveCall()
    if (orderID) {
      calls.setCurrentCall(null)
      calls.setIncomingCall(null)
      calls.setOutgoingCall(null)
      calls.setEndingCallOrderID(null)
      usePresenceStore.getState().setCallStatus('idle')
    }
  }

  const cleanupHiddenPageCall = () => {
    if (document.visibilityState === 'visible') {
      clearHiddenCleanupTimer()
      return
    }
    if (document.visibilityState !== 'hidden' || hiddenCleanupTimer !== null || cleanedUp) {
      return
    }
    hiddenCleanupTimer = setTimeout(() => {
      hiddenCleanupTimer = null
      if (document.visibilityState === 'hidden') {
        cleanupMedia()
      }
    }, HIDDEN_CALL_CLEANUP_DELAY_MS)
  }

  const cancelHiddenPageCleanup = (event?: PageTransitionEvent) => {
    if (!event || event.persisted || document.visibilityState === 'visible') {
      clearHiddenCleanupTimer()
    }
  }

  window.addEventListener('pagehide', cleanupMedia)
  window.addEventListener('pageshow', cancelHiddenPageCleanup)
  document.addEventListener('visibilitychange', cleanupHiddenPageCall)

  return () => {
    clearHiddenCleanupTimer()
    window.removeEventListener('pagehide', cleanupMedia)
    window.removeEventListener('pageshow', cancelHiddenPageCleanup)
    document.removeEventListener('visibilitychange', cleanupHiddenPageCall)
  }
}
